import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { 
  Phone, Mail, MapPin, Send, Microscope, FlaskConical,
  Building2, Users, Calendar, Award, ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  COMPANY, HOME_PAGE, BRAND_PARTNERS, ABOUT_PAGE, 
  PRODUCTS_PAGE, CONTACT_PAGE 
} from "../config/content";
import Header from "./Header";
import Footer from "./Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statIcons = [Calendar, Users, Building2, Award];

export default function LandingPage() {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "", message: "", website: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Thank you! Your inquiry has been submitted successfully.");
      setFormData({ name: "", email: "", phone: "", company: "", message: "", website: "" });
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Header />

      {/* Hero Section */}
      <section className="pt-20 md:pt-24 min-h-screen flex items-center bg-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <motion.p 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="overline text-[#0047FF] mb-4"
              >
                {HOME_PAGE.hero.overline}
              </motion.p>
              <motion.h1 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 mb-6"
              >
                {HOME_PAGE.hero.title}
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-base md:text-lg text-slate-600 leading-relaxed mb-8 max-w-xl"
              >
                {HOME_PAGE.hero.description}
              </motion.p>
              <motion.div 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-wrap gap-4"
              >
                <Button 
                  onClick={() => scrollToSection("contact")} 
                  className="bg-[#0047FF] hover:bg-[#0037CC] text-white rounded-none px-8 py-6 text-base"
                >
                  {HOME_PAGE.hero.primaryButton} <ArrowRight className="ml-2" size={18} />
                </Button>
                <Link to="/products">
                  <Button variant="outline" className="rounded-none px-8 py-6 text-base border-slate-300 hover:bg-slate-50">
                    {HOME_PAGE.hero.secondaryButton}
                  </Button>
                </Link>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-12 pt-8 border-t border-slate-200"
              >
                <p className="overline mb-4">Verticals We Serve</p>
                <div className="flex flex-wrap gap-3">
                  {HOME_PAGE.verticals.map((vertical) => (
                    <span key={vertical} className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium">
                      {vertical}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>

            <motion.div 
              initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <img 
                  src={HOME_PAGE.hero.image}
                  alt="Modern laboratory"
                  className="w-full aspect-[4/5] object-cover"
                />
                <div className="absolute -bottom-4 -left-4 bg-[#0047FF] text-white p-6">
                  <p className="text-3xl font-black font-heading">{COMPANY.yearsInBusiness}</p>
                  <p className="text-sm opacity-90">Years of Excellence</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brands Marquee */}
      <section id="brands" className="py-16 md:py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
          <p className="overline text-center">Trusted by the World's Leading Brands</p>
        </div>
        <Marquee gradient={false} speed={40} pauseOnHover={true}>
          <div className="flex items-center gap-12 md:gap-16 px-8">
            {BRAND_PARTNERS.map((brand) => (
              <a key={brand.name} href={brand.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center h-16 md:h-20 min-w-[120px] md:min-w-[160px] grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all duration-300">
                <img src={brand.logo} alt={brand.name} className="h-full w-auto max-w-[140px] md:max-w-[180px] object-contain" loading="lazy" />
              </a>
            ))}
          </div>
        </Marquee>
      </section>

      {/* About/Stats Section */}
      <section id="about" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-slate-200">
            <div className="lg:col-span-8 bg-white p-8 md:p-12">
              <p className="overline text-[#0047FF] mb-4">About Us</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                {ABOUT_PAGE.hero.subtitle}
              </h2>
              {ABOUT_PAGE.whoWeAre.paragraphs.slice(0, 2).map((para, index) => (
                <p key={index} className="text-slate-600 leading-relaxed mb-4">{para}</p>
              ))}
              <Link to="/about">
                <Button variant="outline" className="rounded-none mt-4">
                  Learn More About Us <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-4 grid grid-cols-2 bg-slate-200 gap-px">
              {HOME_PAGE.stats.map((stat, index) => {
                const Icon = statIcons[index] || Award;
                return (
                  <motion.div key={stat.label}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white p-6 md:p-8 flex flex-col justify-center"
                  >
                    <Icon className="w-6 h-6 text-[#0047FF] mb-4" />
                    <p className="stat-number text-4xl md:text-5xl text-slate-900 mb-2">{stat.number}</p>
                    <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">{stat.label}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="overline text-[#0047FF] mb-4">Our Products</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Comprehensive Laboratory Solutions
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 border border-slate-200">
            {PRODUCTS_PAGE.categories.map((product, index) => {
              const Icon = index === 0 ? Microscope : FlaskConical;
              return (
                <motion.div key={product.id}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`product-card bg-white p-8 md:p-10 ${index === 0 ? 'border-b md:border-b-0 md:border-r' : ''} border-slate-200 hover:bg-slate-50 transition-colors`}
                >
                  <div className="aspect-video mb-6 overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <Icon className="w-6 h-6 text-[#0047FF]" />
                    <h3 className="font-heading text-xl md:text-2xl font-semibold text-slate-900">{product.name}</h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-4">{product.description}</p>
                  <Link to="/products" className="text-[#0047FF] font-medium hover:underline">
                    View Details →
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-32 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <p className="overline text-[#0047FF] mb-4">Get In Touch</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
                Request a Quote
              </h2>
              <p className="text-slate-600 mb-8">
                Fill out the form and our team will get back to you within 24 hours.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input type="text" name="name" placeholder="Your Name *" value={formData.name} onChange={handleInputChange} required className="rounded-none" />
                  <Input type="email" name="email" placeholder="Email *" value={formData.email} onChange={handleInputChange} required className="rounded-none" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input type="tel" name="phone" placeholder="Phone" value={formData.phone} onChange={handleInputChange} className="rounded-none" />
                  <Input type="text" name="company" placeholder="Company" value={formData.company} onChange={handleInputChange} className="rounded-none" />
                </div>
                <Textarea name="message" placeholder="Your requirements *" value={formData.message} onChange={handleInputChange} required rows={5} className="rounded-none resize-none" />
                <div className="absolute -left-[9999px] opacity-0 pointer-events-none" aria-hidden="true">
                  <Input type="text" name="website" value={formData.website} onChange={handleInputChange} tabIndex={-1} autoComplete="off" />
                </div>
                <Button type="submit" disabled={isSubmitting} className="w-full bg-[#0047FF] hover:bg-[#0037CC] text-white rounded-none py-6 text-base">
                  {isSubmitting ? "Submitting..." : <><Send size={18} className="mr-2" /> Send Inquiry</>}
                </Button>
              </form>
            </div>
            <div>
              <p className="overline text-[#0047FF] mb-4">Our Locations</p>
              <h3 className="font-heading text-2xl font-bold tracking-tight text-slate-900 mb-8">Visit Us</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {CONTACT_PAGE.offices.slice(0, 4).map((office, index) => (
                  <motion.div key={office.name}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="contact-card bg-slate-50 border border-slate-200 p-4 hover:border-[#0047FF] transition-colors"
                  >
                    <h4 className="font-semibold text-slate-900 mb-2 text-sm">{office.name}</h4>
                    <p className="text-xs text-slate-600 mb-3 leading-relaxed">{office.address}</p>
                    <a href={`tel:${office.phone.replace(/\s/g, '')}`} className="flex items-center gap-1 text-xs text-slate-600 hover:text-[#0047FF]">
                      <Phone size={12} /> {office.phone}
                    </a>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
